import type { MouseEventHandler, ReactNode } from 'react';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { animated, useSpring } from '@react-spring/web';

import CloseIcon from '../icons/CloseIcon';

import { useAriaId } from '../../hooks/use-aria-id';
import { canUseDOM } from '../../utilities/can-use-dom';
import { Box } from '../box/box';
import { Button } from '../button/button';
import { Card } from '../card/card';
import { Heading } from '../heading/heading';

import { TEST_IDS } from './test-id';

import * as styles from './modal.css';

interface ModalCompoundProps {
  children: ReactNode;
}

export interface ModalProps {
  /**
   * When dismissable is true, adds a close button in the top-right and
   * also closes when clicking outside of the modal.
   * When dismissable is false, can only be closed by actions added
   * to the modal children, or alternatively by pressing the Escape key.
   */
  dismissable?: boolean;
  onClose: () => void;
  /**
   * Text to assist users with screen readers when the close button is focussed
   */
  closeDialogText: ReactNode;
  open: boolean;
  children: ReactNode;
}

// React portals are not supported on the server, so we need to return null in those cases
const clientCreatePortal = canUseDOM
  ? (children: ReactNode) => createPortal(<>{children}</>, window.document.body)
  : () => null;

const IdContext = createContext('');

/**
 * For use as first child of <Modal />, gives the modal an accessible title
 */
const ModalTitle = ({ children }: ModalCompoundProps) => {
  const id = useContext(IdContext);

  return (
    <Heading id={id} level="2" variant="bodyMedium150">
      {children}
    </Heading>
  );
};

/**
 * For use as final child of <Modal /> to contain all action buttons
 */
const ModalActions = ({ children }: ModalCompoundProps) => (
  <Box stack="row" spacing="base" className={styles.modalActions}>
    {children}
  </Box>
);

/**
 * For use inside <ModalActions />, closes the dialog using the form
 */
const ModalCloseButton = ({ children }: ModalCompoundProps) => (
  <Button type="submit" value="close" variant="outline">
    {children}
  </Button>
);

const useMounted = (open: boolean) => {
  // By setting this mounted flag after the DOM has been rendered we can
  // avoid a hydration warning. This is because the dialog element is
  // inside a portal and this is not rendered on the server
  const [mounted, setMounted] = useState(false);
  if (open && !mounted) {
    setMounted(true);
  }
  return mounted;
};

export const Modal = ({
  dismissable = false,
  children,
  open,
  onClose,
  closeDialogText,
}: ModalProps) => {
  // The headingId links the modal with the heading for good a11y
  const headingId = useAriaId('modal-heading');
  const dialogRef = useRef<HTMLDialogElement>(null);

  const mounted = useMounted(open);

  const animatedStyles = useSpring({
    config: open
      ? {
          mass: 1,
          tension: 150,
          friction: 18,
          clamp: true,
          velocity: 0.001, // seconds
        }
      : {
          velocity: 0, // seconds
        },
    opacity: open ? 1 : 0,
    transform: open ? 'scale(1)' : 'scale(0.9)',
  });

  useEffect(() => {
    if (open) dialogRef.current?.showModal();

    // We need to track if outside state is declaring the modal closed, if so we
    // need to manually close the html dialog if open
    if (!open && dialogRef.current?.open) dialogRef.current?.close();
  }, [open]);

  const closeOnOutsideClick: MouseEventHandler<HTMLDialogElement> | undefined =
    dismissable
      ? ({ currentTarget, target }) => {
          const hasClickedOutsideModal =
            target === currentTarget && target === dialogRef.current;

          hasClickedOutsideModal && dialogRef.current?.close();
        }
      : undefined;

  return (
    <IdContext.Provider value={headingId}>
      <>
        {mounted &&
          clientCreatePortal(
            <animated.dialog
              aria-labelledby={headingId}
              className={styles.dialog}
              style={animatedStyles}
              ref={dialogRef}
              onClose={(e) => {
                // Prevent the event from bubbling up to the window
                // This will stop e.g. the modal from closing a Drawer component
                e.stopPropagation();

                onClose();
              }}
              onClick={closeOnOutsideClick}
              data-testid={TEST_IDS.DIALOG}
            >
              <Card
                Component="form"
                method="dialog"
                variant="lightShadow"
                spacing="base"
              >
                {children}
                {dismissable ? (
                  <Button
                    className={styles.close}
                    variant="icon"
                    value="cancel"
                    type="submit"
                    Icon={CloseIcon}
                    iconColor="neutral500"
                    size="medium"
                  >
                    {closeDialogText}
                  </Button>
                ) : null}
              </Card>
            </animated.dialog>,
          )}
      </>
    </IdContext.Provider>
  );
};

Modal.Actions = ModalActions;
Modal.Title = ModalTitle;
Modal.CloseButton = ModalCloseButton;
